import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';


// Admin Assessment API - Assessment management functions
export const adminAssessmentAPI = {
  // Initialize assessments
  initializeAssessments: async (force = false, type = '360', languages = ['he', 'en']) => {
    const initializeAssessmentsFunction = httpsCallable(functions, 'initializeAssessments');
    const result = await initializeAssessmentsFunction({ force, type, languages });
    return { data: result.data };
  },

  // Create custom assessment
  createAssessment: async (assessmentData: any) => {
    const createAssessmentFunction = httpsCallable(functions, 'createAssessment');
    const result = await createAssessmentFunction(assessmentData);
    return { data: result.data };
  },

  // Update assessment
  updateAssessment: async (id: string, updateData: any) => {
    const updateAssessmentFunction = httpsCallable(functions, 'updateAssessment');
    const result = await updateAssessmentFunction({ id, ...updateData });
    return { data: result.data };
  },

  // Toggle assessment status
  toggleAssessmentStatus: async (id: string) => {
    const toggleAssessmentStatusFunction = httpsCallable(functions, 'toggleAssessmentStatus');
    const result = await toggleAssessmentStatusFunction({ id });
    return { data: result.data };
  },

  // List assessments with filters
  listAssessments: async (filters?: any) => {
    const listAssessmentsFunction = httpsCallable(functions, 'listAssessments');
    const result = await listAssessmentsFunction(filters || {});
    return { data: result.data };
  },
};

// Admin Session API - Only admin functions
export const adminSessionAPI = {
  // Get completed sessions using Firebase Function (onCall)
  getCompletedSessions: async (organization?: string) => {
    const getCompletedSessionsFunction = httpsCallable(functions, 'getCompletedSessions');
    const result = await getCompletedSessionsFunction({ organization });
    return { data: result.data };
  },

  // Get unfinished sessions (started, in_progress)
  getUnfinishedSessions: async (organization?: string) => {
    const getUnfinishedSessionsFunction = httpsCallable(functions, 'getUnfinishedSessions');
    const result = await getUnfinishedSessionsFunction({ organization });
    return { data: result.data };
  },

  // Get individual session details with results
  getSessionDetails: async (sessionId: string) => {
    const getSessionDetailsFunction = httpsCallable(functions, 'getSessionDetails');
    const result = await getSessionDetailsFunction({ sessionId });
    return { data: result.data };
  },

  // Get completed sessions with charts
  getCompletedSessionsWithCharts: async (language?: 'he' | 'en') => {
    const getCompletedSessionsWithChartsFunction = httpsCallable(functions, 'getCompletedSessionsWithCharts');
    const result = await getCompletedSessionsWithChartsFunction({ language });
    return { data: result.data };
  },

  // Generate chart for single session
  generateChart: async (id: string, language?: 'he' | 'en') => {
    const generateChartFunction = httpsCallable(functions, 'generateChart');
    const result = await generateChartFunction({ sessionId: id, language: language || 'he' });
    return { data: result.data };
  },

  // Get organizations (now using onCall)
  getOrganizations: async () => {
    const getOrganizationsFunction = httpsCallable(functions, 'getOrganizations');
    const result = await getOrganizationsFunction();
    return { data: (result.data as any).organizations };
  },

  // Generate comparison chart
  generateComparisonChart: async (sessionIds: string[], language?: 'he' | 'en') => {
    const generateComparisonChartFunction = httpsCallable(functions, 'generateComparisonChart');
    const result = await generateComparisonChartFunction({ sessionIds, language: language || 'he' });
    return { data: result.data };
  },

  // Export sessions to CSV
  exportSessionsToCSV: async (sessionIds?: string[], includeResponses = false, format = 'csv') => {
    const exportSessionsToCSVFunction = httpsCallable(functions, 'exportSessionsToCSV');
    const result = await exportSessionsToCSVFunction({ sessionIds, includeResponses, format });
    return { data: result.data };
  },

  // Advanced search
  searchSessions: async (filters: any) => {
    const searchSessionsFunction = httpsCallable(functions, 'searchSessions');
    const result = await searchSessionsFunction(filters);
    return { data: result.data };
  },

  // Send assessment notification
  sendAssessmentNotification: async (sessionId: string, recipientEmail: string, includeResults = true, customMessage?: string) => {
    const sendAssessmentNotificationFunction = httpsCallable(functions, 'sendAssessmentNotification');
    const result = await sendAssessmentNotificationFunction({ sessionId, recipientEmail, includeResults, customMessage });
    return { data: result.data };
  },
};

// Admin Quiz Leads API
export const adminQuizLeadsAPI = {
  // Get all quiz leads
  getQuizLeads: async (status?: string, limit?: number) => {
    const getQuizLeadsFunction = httpsCallable(functions, 'getQuizLeads');
    const result = await getQuizLeadsFunction({ status, limit });
    return { data: result.data };
  },

  // Update lead status
  updateLeadStatus: async (leadId: string, status: string, notes?: string) => {
    const updateQuizLeadStatusFunction = httpsCallable(functions, 'updateQuizLeadStatus');
    const result = await updateQuizLeadStatusFunction({ leadId, status, notes });
    return { data: result.data };
  },
};

// Admin Invite API - Only admin functions
export const adminInviteAPI = {
  // Create invite (admin only)
  createInvite: async (companyId: string, assessmentId: string, ttlMinutes = 120) => {
    const createInviteFunction = httpsCallable(functions, 'createInvite');
    const result = await createInviteFunction({ companyId, assessmentId, ttlMinutes });
    return result.data;
  },

  // List invites (admin only)
  listInvites: async (companyId: string, assessmentId: string) => {
    const listInvitesFunction = httpsCallable(functions, 'listInvites');
    const result = await listInvitesFunction({ companyId, assessmentId });
    return result.data;
  },

  // Expire invite (admin only)
  expireInvite: async (companyId: string, assessmentId: string, inviteId: string) => {
    const expireInviteFunction = httpsCallable(functions, 'expireInvite');
    const result = await expireInviteFunction({ companyId, assessmentId, inviteId });
    return result.data;
  },

  // Set admin claim (server admin only)
  setAdminClaim: async (uid: string, isAdmin = true) => {
    const setAdminClaimFunction = httpsCallable(functions, 'setAdminClaim');
    const result = await setAdminClaimFunction({ uid, isAdmin });
    return result.data;
  },
};

// Real-time listeners using polling (admin requires Firebase Functions for all data access)
export const adminRealtimeAPI = {
  // Poll for completed sessions (admin version)
  listenToCompletedSessions: (callback: (sessions: any[]) => void) => {
    // Use polling with Firebase Functions instead of direct Firestore access
    const pollInterval = setInterval(async () => {
      try {
        const getCompletedSessionsFunction = httpsCallable(functions, 'getCompletedSessions');
        const result = await getCompletedSessionsFunction({});
        callback(result.data as any[]);
      } catch (error) {
        console.error('Error polling completed sessions:', error);
      }
    }, 5000); // Poll every 5 seconds

    // Return cleanup function
    return () => clearInterval(pollInterval);
  },

  // Poll for organizations (admin version)
  listenToOrganizations: (callback: (organizations: string[]) => void) => {
    // Use polling with Firebase Functions instead of direct Firestore access
    const pollInterval = setInterval(async () => {
      try {
        const getOrganizationsFunction = httpsCallable(functions, 'getOrganizations');
        const result = await getOrganizationsFunction();
        callback((result.data as any).organizations || []);
      } catch (error) {
        console.error('Error polling organizations:', error);
      }
    }, 10000); // Poll every 10 seconds

    // Return cleanup function
    return () => clearInterval(pollInterval);
  },
};

// Error handling wrapper
const handleFirebaseError = (error: any) => {
  console.error('Firebase API Error:', error);
  
  if (error.code) {
    switch (error.code) {
      case 'functions/not-found':
        throw new Error('The requested function was not found');
      case 'functions/permission-denied':
        throw new Error('Permission denied - Admin access required');
      case 'functions/unauthenticated':
        throw new Error('Authentication required');
      case 'functions/resource-exhausted':
        throw new Error('Service temporarily unavailable');
      case 'functions/internal':
        throw new Error('Internal server error');
      default:
        throw new Error(error.message || 'An unknown error occurred');
    }
  }
  
  throw error;
};

// Wrap all API calls with error handling
const createErrorWrapper = (apiObj: any) => {
  const wrapped: any = {};
  for (const key in apiObj) {
    if (typeof apiObj[key] === 'function') {
      wrapped[key] = async (...args: any[]) => {
        try {
          return await apiObj[key](...args);
        } catch (error) {
          handleFirebaseError(error);
        }
      };
    } else if (typeof apiObj[key] === 'object') {
      wrapped[key] = createErrorWrapper(apiObj[key]);
    } else {
      wrapped[key] = apiObj[key];
    }
  }
  return wrapped;
};

// Export wrapped APIs
export const wrappedAdminSessionAPI = createErrorWrapper(adminSessionAPI);
export const wrappedAdminInviteAPI = createErrorWrapper(adminInviteAPI);
export const wrappedAdminRealtimeAPI = createErrorWrapper(adminRealtimeAPI);
export const wrappedAdminQuizLeadsAPI = createErrorWrapper(adminQuizLeadsAPI);

// Admin Organization API
export const adminOrganizationAPI = {
  // Create or update organization
  upsertOrganization: async (orgData: any) => {
    const upsertOrganizationFunction = httpsCallable(functions, 'upsertOrganization');
    const result = await upsertOrganizationFunction(orgData);
    return { data: result.data };
  },

  // List organizations with stats
  listOrganizationsWithStats: async () => {
    const listOrganizationsWithStatsFunction = httpsCallable(functions, 'listOrganizationsWithStats');
    const result = await listOrganizationsWithStatsFunction();
    return { data: result.data };
  },
};

// Admin Report API
export const adminReportAPI = {
  // Generate organization report
  generateOrganizationReport: async (organization: string, dateFrom?: string, dateTo?: string, includeCharts = true, language = 'he') => {
    const generateOrganizationReportFunction = httpsCallable(functions, 'generateOrganizationReport');
    const result = await generateOrganizationReportFunction({ organization, dateFrom, dateTo, includeCharts, language });
    return { data: result.data };
  },
};

// Admin Data Management API
export const adminDataAPI = {
  // Cleanup old sessions
  cleanupOldSessions: async (retentionDays = 365, dryRun = true) => {
    const cleanupOldSessionsFunction = httpsCallable(functions, 'cleanupOldSessions');
    const result = await cleanupOldSessionsFunction({ retentionDays, dryRun });
    return { data: result.data };
  },

  // Get audit logs
  getAuditLogs: async (filters?: any) => {
    const getAuditLogsFunction = httpsCallable(functions, 'getAuditLogs');
    const result = await getAuditLogsFunction(filters || {});
    return { data: result.data };
  },
};

// Admin Test Mode API
export const adminTestAPI = {
  // Create test session
  createTestSession: async (assessmentId: string) => {
    const createTestSessionFunction = httpsCallable(functions, 'createTestSession');
    const result = await createTestSessionFunction({ assessmentId });
    return { data: result.data };
  },

  // Delete test sessions
  deleteTestSessions: async () => {
    const deleteTestSessionsFunction = httpsCallable(functions, 'deleteTestSessions');
    const result = await deleteTestSessionsFunction();
    return { data: result.data };
  },
};

// Admin Consulting Firm API
export const adminFirmAPI = {
  // Create consulting firm
  createConsultingFirm: async (data: { name: string; leadEmail: string; branding?: { firmName?: string; logo?: string } }) => {
    const createConsultingFirmFunction = httpsCallable(functions, 'createConsultingFirm');
    const result = await createConsultingFirmFunction(data);
    return { data: result.data };
  },

  // Update consulting firm
  updateConsultingFirm: async (firmId: string, data: { name?: string; branding?: any; isActive?: boolean }) => {
    const updateConsultingFirmFunction = httpsCallable(functions, 'updateConsultingFirm');
    const result = await updateConsultingFirmFunction({ firmId, ...data });
    return { data: result.data };
  },

  // List consulting firms
  listConsultingFirms: async () => {
    const listConsultingFirmsFunction = httpsCallable(functions, 'listConsultingFirms');
    const result = await listConsultingFirmsFunction();
    return { data: result.data };
  },

  // Delete consulting firm
  deleteConsultingFirm: async (firmId: string) => {
    const deleteConsultingFirmFunction = httpsCallable(functions, 'deleteConsultingFirm');
    const result = await deleteConsultingFirmFunction({ firmId });
    return { data: result.data };
  },

  // Add consultant to firm
  addConsultantToFirm: async (firmId: string, email: string, name?: string) => {
    const addConsultantToFirmFunction = httpsCallable(functions, 'addConsultantToFirm');
    const result = await addConsultantToFirmFunction({ firmId, email, name });
    return { data: result.data };
  },

  // Remove consultant from firm
  removeConsultantFromFirm: async (firmId: string, userId: string) => {
    const removeConsultantFromFirmFunction = httpsCallable(functions, 'removeConsultantFromFirm');
    const result = await removeConsultantFromFirmFunction({ firmId, userId });
    return { data: result.data };
  },

  // Assign client to consultant
  assignClientToConsultant: async (firmId: string, consultantId: string, organizationId: string) => {
    const assignClientToConsultantFunction = httpsCallable(functions, 'assignClientToConsultant');
    const result = await assignClientToConsultantFunction({ firmId, consultantId, organizationId });
    return { data: result.data };
  },

  // Unassign client from consultant
  unassignClientFromConsultant: async (firmId: string, consultantId: string, organizationId: string) => {
    const unassignClientFromConsultantFunction = httpsCallable(functions, 'unassignClientFromConsultant');
    const result = await unassignClientFromConsultantFunction({ firmId, consultantId, organizationId });
    return { data: result.data };
  },

  // Create client organization
  createClientOrganization: async (firmId: string, name: string, language?: 'he' | 'en') => {
    const createClientOrganizationFunction = httpsCallable(functions, 'createClientOrganization');
    const result = await createClientOrganizationFunction({ firmId, name, language });
    return { data: result.data };
  },

  // Get firm dashboard
  getFirmDashboard: async (firmId: string) => {
    const getFirmDashboardFunction = httpsCallable(functions, 'getFirmDashboard');
    const result = await getFirmDashboardFunction({ firmId });
    return { data: result.data };
  },

  // Get firm consultants
  getFirmConsultants: async (firmId: string) => {
    const getFirmConsultantsFunction = httpsCallable(functions, 'getFirmConsultants');
    const result = await getFirmConsultantsFunction({ firmId });
    return { data: result.data };
  },

  // Get firm clients
  getFirmClients: async (firmId: string) => {
    const getFirmClientsFunction = httpsCallable(functions, 'getFirmClients');
    const result = await getFirmClientsFunction({ firmId });
    return { data: result.data };
  },
};

// Export wrapped APIs
export const wrappedAdminAssessmentAPI = createErrorWrapper(adminAssessmentAPI);
export const wrappedAdminOrganizationAPI = createErrorWrapper(adminOrganizationAPI);
export const wrappedAdminReportAPI = createErrorWrapper(adminReportAPI);
export const wrappedAdminDataAPI = createErrorWrapper(adminDataAPI);
export const wrappedAdminTestAPI = createErrorWrapper(adminTestAPI);
export const wrappedAdminFirmAPI = createErrorWrapper(adminFirmAPI);

// Default exports
const adminAPIDefault = {
  adminAssessmentAPI: wrappedAdminAssessmentAPI,
  adminSessionAPI: wrappedAdminSessionAPI,
  adminInviteAPI: wrappedAdminInviteAPI,
  adminRealtimeAPI: wrappedAdminRealtimeAPI,
  adminOrganizationAPI: wrappedAdminOrganizationAPI,
  adminReportAPI: wrappedAdminReportAPI,
  adminDataAPI: wrappedAdminDataAPI,
  adminTestAPI: wrappedAdminTestAPI,
  adminFirmAPI: wrappedAdminFirmAPI,
};

export default adminAPIDefault;